import { useTranslation } from "react-i18next";
import { Container, Label, Message } from "./searchableDropdown.styles";
import { SearchableDropdownProps } from "./searchableDropdown.types";
import { useSearchableDropdown } from "./useSearchableDropdown";
import { SearchableDropdownInput } from "./searchableDropdownInput";
import { SearchableDropdownList } from "./searchableDropdownList";

const DEFAULT_MENU_Z_INDEX = 1000;

export const SearchableDropdown = (
  props: SearchableDropdownProps,
): JSX.Element => {
  const { t } = useTranslation();
  const placeholderText = props.placeholder ?? t("General.SelectOption") ?? "";
  const hook = useSearchableDropdown(props, placeholderText);

  return (
    <Container
      ref={hook.containerRef}
      aria-disabled={props.disabled}
      $size={hook.size}
      $fullWidth={props.fullWidth ?? false}
    >
      {props.label && <Label id={hook.labelId}>{props.label}</Label>}
      <SearchableDropdownInput
        label={props.label}
        labelId={hook.labelId}
        listboxId={hook.listboxId}
        placeholderText={hook.placeholderText}
        leadingIconName={props.leadingIconName}
        disabled={props.disabled}
        size={hook.size}
        visualState={hook.visualState}
        isOpen={hook.isOpen}
        highlightedIndex={hook.highlightedIndex}
        resolvedValue={hook.resolvedValue}
        showExtraCountBadge={hook.showExtraCountBadge}
        multiSelectDisplay={hook.multiSelectDisplay}
        canClear={hook.canClear}
        inputRef={hook.inputRef}
        onOpenDropdown={hook.openDropdown}
        onCloseDropdown={hook.closeDropdown}
        onInputChange={hook.handleInputChange}
        onKeyDown={hook.handleKeyDown}
        onClear={(event) => {
          event.stopPropagation();
          if (props.disabled) return;
          props.onClear?.();
          hook.setQuery("");
          hook.setHighlightedIndex(-1);
        }}
        onToggleClick={() => {
          if (props.disabled) return;
          if (hook.isOpen) {
            hook.closeDropdown();
            return;
          }
          hook.openDropdown();
          hook.inputRef.current?.focus();
        }}
      />
      {(hook.visualState === "error" ? props.errorText : props.hintText) && (
        <Message $state={hook.visualState}>
          {hook.visualState === "error"
            ? (props.errorText ?? t("DesignComponents.DefaultError"))
            : props.hintText}
        </Message>
      )}
      <SearchableDropdownList
        listboxId={hook.listboxId}
        labelId={props.label ? hook.labelId : undefined}
        isOpen={hook.isOpen}
        zIndex={DEFAULT_MENU_Z_INDEX}
        multiSelect={props.multiSelect}
        filteredOptions={hook.filteredOptions}
        highlightedIndex={hook.highlightedIndex}
        selectedValueSet={hook.selectedValueSet}
        value={props.value}
        emptyText={props.emptyText ?? t("General.NoResults")}
        onSelect={hook.handleSelect}
      />
    </Container>
  );
};
